import React, { useState } from "react";
import { Link } from "react-router-dom";
import { FiChevronDown, FiChevronRight, FiCopy } from "react-icons/fi";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "../../components/ui/collapsible";
import { useToaster } from "../../contexts/ToasterContext";
import { FileText } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { STATIC_FILE_EXTENSIONS } from "./staticData";
import ConfigManagement from "./ConfigManagement";
import DownloadReport from "./DownloadReport";
import UsersSettings from "./UsersSettings";
import { showFreeAssessmentToast } from "../../utils/freeAssessmentRedirect";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '../../components/ui/breadcrumb';

// Scan-related settings moved to ManageScansPage

// SelectionPanel moved to ManageScansPage

// Configuration categories
const configCategories = [
  { key: 'cloudStorage', title: 'Cloud & Storage' },
  { key: 'vcs', title: 'Version Control System' },
  { key: 'communication', title: 'Communication Services' },
  { key: 'vault', title: 'Vaults' },
  { key: 'aiModel', title: 'AI Models (Remediation)' },
  { key: 'ticketing', title: 'Ticketing Service' }
];

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('config');
  const [configExpanded, setConfigExpanded] = useState(true); // Start expanded
  const [selectedConfigCategory, setSelectedConfigCategory] = useState('cloudStorage');
  const [showCreateAccountDialog, setShowCreateAccountDialog] = useState(false);
  
  const handleLogout = () => {
    // Clear localStorage
    localStorage.removeItem('jwt');
    localStorage.removeItem('user');
    // Redirect to login page
    window.location.href = '/login';
  };

  const LeftNav = () => (
    <div
      className="w-64 shrink-0 border-r border-gray-200 pr-0 md:pr-4"
      data-tour="settings-nav"
    >
      <div className="sticky top-4 space-y-2">
        {/* Configuration with collapsible categories */}
        <Collapsible open={configExpanded} onOpenChange={setConfigExpanded}>
          <CollapsibleTrigger asChild>
            <button
              className={`w-full flex items-center justify-between px-3 py-2 rounded-md transition ${
                activeTab === 'config' ? 'bg-[#44087D] text-white' : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
              }`}
              data-tour="settings-config"
              onClick={() => {
                setActiveTab('config');
                setConfigExpanded(!configExpanded);
              }}
            >
              <span>Configuration</span>
              {configExpanded ? (
                <FiChevronDown className="h-4 w-4" />
              ) : (
                <FiChevronRight className="h-4 w-4" />
              )}
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div
              className="ml-4 mt-2 space-y-1"
              data-tour="settings-config-categories"
            >
              {configCategories.map((category) => (
                <button
                  key={category.key}
                  className={`w-full text-left px-3 py-2 rounded-md transition text-sm ${
                    selectedConfigCategory === category.key
                      ? 'bg-[#44087D]/20 text-[#44087D] border-l-2 border-[#44087D]'
                      : 'hover:bg-gray-100 text-gray-600'
                  }`}
                  onClick={() => setSelectedConfigCategory(category.key)}
                >
                  {category.title}
                </button>
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
        
        {/* Preferences button */}
        <button
          className={`w-full text-left px-3 py-2 rounded-md transition ${
            activeTab === 'preferences' ? 'bg-[#44087D] text-white' : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
          }`}
          onClick={() => {
            setActiveTab('preferences');
            setConfigExpanded(false);
            setSelectedConfigCategory(null);
          }}
        >
          Preferences
        </button>
        
        {/* Reports button */}
        <button
          className={`w-full text-left px-3 py-2 rounded-md transition ${
            activeTab === 'reports' ? 'bg-[#44087D] text-white' : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
          }`}
          data-tour="settings-reports"
          onClick={() => {
            setActiveTab('reports');
            setConfigExpanded(false);
            setSelectedConfigCategory(null);
          }}
        >
          Reports
        </button>
        
        {/* Users button */}
        <button
          className={`w-full text-left px-3 py-2 rounded-md transition ${
            activeTab === 'users' ? 'bg-[#44087D] text-white' : 'bg-gray-50 hover:bg-gray-100 text-gray-800'
          }`}
          data-tour="settings-access-control"
          onClick={() => {
            setActiveTab('users');
            setConfigExpanded(false);
            setSelectedConfigCategory(null);
          }}
        >
          Access Control
        </button>
        
      </div>
    </div>
  );

  const ConfigPanel = () => (
    <div className="flex-1" data-tour="settings-config-management">
      {selectedConfigCategory ? (
        <div className="mb-8">
          <div className="mb-4">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-800">
                  {configCategories.find(cat => cat.key === selectedConfigCategory)?.title}
                </h2>
                <p className="text-gray-600 mt-1">Configure your {configCategories.find(cat => cat.key === selectedConfigCategory)?.title.toLowerCase()} settings</p>
              </div>
              {selectedConfigCategory === 'cloudStorage' && (
                <Button
                  size="sm"
                  onClick={() => {
                    showFreeAssessmentToast();
                  }}
                  className="border border-[#44087D] bg-[#44087D]/10 text-[#44087D]"
                >
                  Create Account
                </Button>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="mb-8">
          <div className="text-center py-12">
            <div className="text-gray-400 mb-4">
              <svg
                className="w-16 h-16 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={1}
                  d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                />
              </svg>
              <h3 className="text-lg font-medium text-gray-400 mb-2">
                Select a Configuration Category
              </h3>
              <p className="text-gray-400">
                Choose a category from the sidebar to begin configuring your services
              </p>
            </div>
          </div>
        </div>
      )}

    </div>
  );

  const UsersPanel = () => (
    <div className="flex-1" data-tour="settings-users-panel">
      <UsersSettings />
    </div>
  );

  // File Extensions Component
  const FileExtensionsComponent = () => {
    const { showToast } = useToaster();
    const [fileExtensions, setFileExtensions] = useState(STATIC_FILE_EXTENSIONS);
    const [editing, setEditing] = useState(false);
    const [editingExtensions, setEditingExtensions] = useState([]);

    const handleEdit = () => {
      setEditingExtensions([...fileExtensions]);
      setEditing(true);
    };

    const handleSave = () => {
      // Validate extensions
      if (editingExtensions.length === 0) {
        showToast("At least one file extension is required", "error");
        return;
      }
      
      // Normalize extensions (remove duplicates, ensure they start with .)
      const normalizedExtensions = [...new Set(editingExtensions.map(ext => 
        ext.startsWith('.') ? ext.toLowerCase() : `.${ext.toLowerCase()}`
      ))];
      
      setFileExtensions(normalizedExtensions);
      setEditing(false);
      showToast("File extensions updated successfully", "success");
    };

    const handleCancel = () => {
      setEditingExtensions([...fileExtensions]);
      setEditing(false);
    };


    const removeExtension = (index) => {
      setEditingExtensions(editingExtensions.filter((_, i) => i !== index));
    };

    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <FileText className="w-5 h-5" />
              File Extensions
            </CardTitle>
            <p className="text-sm text-gray-600 mt-1">Manage file extensions for security scans</p>
          </div>
          {!editing && (
            <Button
              size="sm"
              onClick={handleEdit}
              className="border border-[#44087D] bg-[#44087D]/10 text-[#44087D] hover:bg-[#44087D]/20"
            >
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {editing ? (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {editingExtensions.map((ext, index) => (
                  <Badge key={index} variant="outline" className="flex items-center gap-1">
                    {ext}
                    <button
                      onClick={() => removeExtension(index)}
                      className="ml-1 hover:bg-gray-200 rounded-full p-0.5"
                    >
                      <FiCopy className="h-3 w-3 rotate-45" />
                    </button>
                  </Badge>
                ))}
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button onClick={handleCancel} variant="outline" size="sm">
                  Cancel
                </Button>
                <Button onClick={handleSave} className="bg-green-600 hover:bg-green-700" size="sm">
                  Save
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex flex-wrap gap-2">
                {fileExtensions.length > 0 ? (
                  fileExtensions.map((ext, index) => (
                    <Badge key={index} variant="outline">
                      {ext}
                    </Badge>
                  ))
                ) : (
                  <p className="text-gray-500 italic">No file extensions configured</p>
                )}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    );
  };

  const PreferencesPanel = () => (
    <div className="flex-1">
      {/* SecuritySettings removed - Session Management moved to bottom of screen */}
    </div>
  );

  // Determine which category to show in ConfigManagement based on active tab
  const getConfigCategory = () => {
    if (activeTab === 'config') {
      return selectedConfigCategory;
    } else if (activeTab === 'preferences') {
      return 'fileExtensions';
    }
    return null;
  };

  // Determine if ConfigManagement should be visible
  const shouldShowConfigManagement = () => {
    return (activeTab === 'config' && selectedConfigCategory) || activeTab === 'preferences';
  };

  const ReportsPanel = () => (
    <div className="flex-1" data-tour="settings-reports-panel">
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-800 mb-4">Reports</h2>
        <p className="text-gray-600 mb-6">Download security reports and analytics</p>
        <div className="bg-white border border-gray-200 shadow-sm p-6 rounded-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold text-gray-900">Security Reports</h3>
            </div>
            <DownloadReport />
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="bg-white m-3 sm:m-4 md:m-6 mt-3 overflow-x-hidden max-w-full min-h-screen">
      <div className="mx-auto max-w-7xl">
        {/* Breadcrumb + Page Title */}
        <div className="mb-4 md:mb-8 space-y-2">
          <div className="flex items-center justify-between">
            <div>
              <Breadcrumb>
                <BreadcrumbList>
                  <BreadcrumbItem>
                    <BreadcrumbLink asChild>
                      <Link to="/">Home</Link>
                    </BreadcrumbLink>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    <BreadcrumbPage>Control Panel</BreadcrumbPage>
                  </BreadcrumbItem>
                </BreadcrumbList>
              </Breadcrumb>
              <h1 className="text-black text-2xl md:text-3xl font-bold">Control Panel</h1>
            </div>
            <Button
              onClick={handleLogout}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-tour="settings-logout"
            >
              Logout
            </Button>
          </div>
        </div>

        <div className="flex gap-6 items-start w-full">
          <LeftNav />
          <div className="flex-1 min-w-0">
            {activeTab === 'config' && <ConfigPanel />}
            {activeTab === 'users' && <UsersPanel />}
            {activeTab === 'preferences' && <PreferencesPanel />}
            {activeTab === 'reports' && <ReportsPanel />}
            
            {/* Single ConfigManagement instance to prevent remounting */}
            {shouldShowConfigManagement() && (
              <ConfigManagement 
                selectedCategory={getConfigCategory()}
                showCreateAccountDialog={showCreateAccountDialog}
                setShowCreateAccountDialog={setShowCreateAccountDialog}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
